"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { ArrowRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { track } from "@vercel/analytics";
import { usePathname } from "next/navigation";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-base font-medium transition-all group disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive aria-invalid:text-destructive cursor-pointer transition-transform duration-200",
  {
    variants: {
      variant: {
        default: "bg-primary text-white shadow-xs hover:bg-primary-600/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-white shadow-xs hover:bg-primary/5 hover:text-primary hover:border-primary",
        secondary:
          "bg-primary-400 text-primary shadow-xs hover:bg-primary-400/90 [&>#arrow-container]:bg-foreground [&>#arrow-container]:text-white",
        light:
          "bg-white text-foreground shadow-xs hover:bg-white/90 [&>#arrow-container]:bg-foreground [&>#arrow-container]:text-white",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        linkSecondary: "text-white underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        xs: "!text-xs",
        sm: "!h-8 gap-1.5 px-3 has-[>svg]:px-2.5 text-xs",
        lg: "h-12 gap-4 rounded-xl px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  showArrow = false,
  trackEvent,
  trackProps,
  onClick,
  ...props
}) {
  const Comp = asChild ? Slot : "button";
  const pathname = usePathname();

  const arrowElement = showArrow ? (
    <div
      className="bg-white rounded-lg text-primary p-1 -mr-1 group-hover:translate-x-1.5 transition-transform duration-200"
      id="arrow-container"
    >
      <ArrowRight />
    </div>
  ) : null;

  // Clone the children element (link) and append the arrow if showArrow is true
  const childrenWithArrow = React.isValidElement(props.children)
    ? React.cloneElement(
        props.children,
        {},
        <>
          {props.children.props.children}
          {arrowElement}
        </>
      )
    : props.children;

  const handleClick = (event) => {
    // Call the original onClick handler if it exists
    if (onClick) {
      onClick(event);
    }
    // Perform tracking if trackEvent is provided
    if (trackEvent) {
      track(trackEvent, { url: pathname, ...trackProps });
    }
  };

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      onClick={handleClick}
      {...props}
    >
      {asChild ? (
        childrenWithArrow
      ) : (
        <>
          {props.children}
          {arrowElement}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
